import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import BrandLogo from '../components/BrandLogo';
import './ApiDocs.css';

/* ── Sidebar nav sections ─────────────────────────────────── */
const SECTIONS = [
  { id: 'overview',        label: 'Overview' },
  { id: 'authentication',  label: 'Authentication' },
  { id: 'rate-limiting',   label: 'Rate Limiting' },
  { id: 'errors',          label: 'Errors' },
  { id: 'versioning',      label: 'Versioning' },
  { id: 'pagination',      label: 'Pagination' },
  { id: 'seo',             label: 'SEO Rank Engine' },
  { id: 'posters',         label: 'AI Poster Forge' },
  { id: 'qr',              label: 'Stylized QRs' },
  { id: 'export',          label: 'Data Export' },
  { id: 'webhooks',        label: 'Webhooks' },
];

/* ── Code block ───────────────────────────────────────────── */
function Code({ lang = 'bash', children }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(children.trim());
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div className="code-block">
      <div className="code-header">
        <span className="code-lang">{lang}</span>
        <button className="copy-btn" onClick={copy}>{copied ? 'Copied!' : 'Copy'}</button>
      </div>
      <pre><code>{children.trim()}</code></pre>
    </div>
  );
}

/* ── Method badge ─────────────────────────────────────────── */
function Method({ type }) {
  return <span className={`method method-${type.toLowerCase()}`}>{type}</span>;
}

/* ── Endpoint row ─────────────────────────────────────────── */
function Endpoint({ method, path, desc, params, response, body }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`endpoint-card ${open ? 'open' : ''}`}>
      <button className="endpoint-header" onClick={() => setOpen(o => !o)}>
        <div className="endpoint-left">
          <Method type={method} />
          <span className="endpoint-path">{path}</span>
        </div>
        <div className="endpoint-right">
          <span className="endpoint-desc">{desc}</span>
          <span className="endpoint-chevron">{open ? '▲' : '▼'}</span>
        </div>
      </button>
      {open && (
        <div className="endpoint-body">
          {params && (
            <div className="ep-section">
              <div className="ep-label">Path / Query Parameters</div>
              <table className="param-table">
                <thead><tr><th>Name</th><th>Type</th><th>Required</th><th>Description</th></tr></thead>
                <tbody>
                  {params.map(p => (
                    <tr key={p.name}>
                      <td><code>{p.name}</code></td>
                      <td><span className="param-type">{p.type}</span></td>
                      <td>{p.req ? <span className="req-yes">Yes</span> : <span className="req-no">No</span>}</td>
                      <td>{p.desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {body && (
            <div className="ep-section">
              <div className="ep-label">Request Body</div>
              <Code lang="json">{body}</Code>
            </div>
          )}
          {response && (
            <div className="ep-section">
              <div className="ep-label">Response</div>
              <Code lang="json">{response}</Code>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Main Component ───────────────────────────────────────── */
export default function ApiDocs() {
  const [active, setActive] = useState('overview');

  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) setActive(e.target.id); }),
      { rootMargin: '-30% 0px -60% 0px' }
    );
    SECTIONS.forEach(s => {
      const el = document.getElementById(s.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  return (
    <div className="docs-root">

      {/* ── Top Nav ──────────────────────────────────────── */}
      <nav className="docs-nav">
        <Link to="/" className="docs-nav-back">← datatallied.web.app</Link>
        <div className="docs-nav-center">
          <BrandLogo className="docs-nav-logo" />
          <span className="docs-nav-title">API Reference</span>
        </div>
        <div className="docs-nav-version">
          <span className="base-url">api.datatallied.com</span>
        </div>
      </nav>

      <div className="docs-layout">

        {/* ── Sidebar ──────────────────────────────────── */}
        <aside className="docs-sidebar">
          <div className="sidebar-section-title">GETTING STARTED</div>
          {SECTIONS.slice(0, 6).map(s => (
            <a key={s.id} href={`#${s.id}`}
               className={`sidebar-link ${active === s.id ? 'active' : ''}`}>
              {s.label}
            </a>
          ))}
          <div className="sidebar-section-title" style={{ marginTop: 24 }}>ENDPOINTS</div>
          {SECTIONS.slice(6).map(s => (
            <a key={s.id} href={`#${s.id}`}
               className={`sidebar-link ${active === s.id ? 'active' : ''}`}>
              {s.label}
            </a>
          ))}
        </aside>

        {/* ── Main Content ─────────────────────────────── */}
        <main className="docs-main">

          {/* OVERVIEW */}
          <section id="overview" className="docs-section">
            <div className="section-eyebrow">Introduction</div>
            <h1 className="docs-h1">Data Tallied REST API</h1>
            <p className="docs-lead">
              The Data Tallied API gives you programmatic access to all platform capabilities —
              SEO rank tracking, AI poster generation, QR creation, data export, and webhook
              event delivery. All endpoints follow REST conventions and return JSON.
            </p>
            <div className="info-box">
              <span className="info-icon">⟨/⟩</span>
              <div>
                <strong>Base URL</strong>
                <Code lang="bash">https://api.datatallied.com/v1</Code>
              </div>
            </div>
            <div className="status-grid">
              {[
                { label: 'Protocol',   val: 'HTTPS only' },
                { label: 'Format',     val: 'JSON' },
                { label: 'Auth',       val: 'Bearer Token' },
                { label: 'Version',    val: 'v1 (stable)' },
              ].map(s => (
                <div key={s.label} className="status-card">
                  <div className="status-label">{s.label}</div>
                  <div className="status-val">{s.val}</div>
                </div>
              ))}
            </div>
          </section>

          {/* AUTHENTICATION */}
          <section id="authentication" className="docs-section">
            <div className="section-eyebrow">Security</div>
            <h2 className="docs-h2">Authentication</h2>
            <p>All API requests must include a valid API key in the <code>Authorization</code> header using the <strong>Bearer</strong> scheme. Keys are generated from your dashboard.</p>
            <Code lang="bash">{`curl https://api.datatallied.com/v1/projects \\
  -H "Authorization: Bearer dt_live_xxxxxxxxxxxxxxxxxxxx"`}</Code>
            <div className="callout warn">
              <strong>Security:</strong> Never expose API keys in client-side code. Use environment variables server-side. Keys prefixed <code>dt_live_</code> are production; <code>dt_test_</code> are sandbox.
            </div>
            <h3 className="docs-h3">Key Scopes</h3>
            <table className="param-table">
              <thead><tr><th>Scope</th><th>Access</th></tr></thead>
              <tbody>
                <tr><td><code>read:all</code></td><td>Read-only access to all resources</td></tr>
                <tr><td><code>write:seo</code></td><td>Create/update SEO rank tasks</td></tr>
                <tr><td><code>write:media</code></td><td>Generate posters and QR codes</td></tr>
                <tr><td><code>write:export</code></td><td>Trigger data exports</td></tr>
                <tr><td><code>write:webhooks</code></td><td>Register and manage webhook endpoints</td></tr>
                <tr><td><code>admin</code></td><td>Full access including key management</td></tr>
              </tbody>
            </table>
          </section>

          {/* RATE LIMITING */}
          <section id="rate-limiting" className="docs-section">
            <div className="section-eyebrow">Limits</div>
            <h2 className="docs-h2">Rate Limiting</h2>
            <p>Rate limits are applied per API key. Limits vary by plan. Responses include the following headers on every request:</p>
            <table className="param-table">
              <thead><tr><th>Header</th><th>Description</th></tr></thead>
              <tbody>
                <tr><td><code>X-RateLimit-Limit</code></td><td>Max requests per window</td></tr>
                <tr><td><code>X-RateLimit-Remaining</code></td><td>Requests remaining in current window</td></tr>
                <tr><td><code>X-RateLimit-Reset</code></td><td>Unix timestamp when window resets</td></tr>
                <tr><td><code>Retry-After</code></td><td>Seconds until you can retry (429 only)</td></tr>
              </tbody>
            </table>
            <div className="plan-grid">
              {[
                { plan: 'Launch',          rpm: '60 req/min',   daily: '5,000/day' },
                { plan: 'Growth',          rpm: '180 req/min',  daily: '25,000/day' },
                { plan: 'Business',        rpm: '300 req/min',  daily: '50,000/day' },
                { plan: 'Revenue Partner', rpm: '1,000 req/min',daily: 'Custom' },
              ].map(p => (
                <div key={p.plan} className="plan-card">
                  <div className="plan-name">{p.plan}</div>
                  <div className="plan-rpm">{p.rpm}</div>
                  <div className="plan-daily">{p.daily}</div>
                </div>
              ))}
            </div>
          </section>

          {/* ERRORS */}
          <section id="errors" className="docs-section">
            <div className="section-eyebrow">Error Handling</div>
            <h2 className="docs-h2">Errors</h2>
            <p>All errors return a consistent JSON body with a machine-readable <code>code</code> and a human-readable <code>message</code>.</p>
            <Code lang="json">{`{
  "error": {
    "code": "invalid_api_key",
    "message": "The API key provided is invalid or has been revoked.",
    "status": 401,
    "docs": "https://api.datatallied.com/docs/errors#invalid_api_key"
  }
}`}</Code>
            <table className="param-table">
              <thead><tr><th>Status</th><th>Code</th><th>Meaning</th></tr></thead>
              <tbody>
                {[
                  ['200', 'ok',                    'Request succeeded'],
                  ['201', 'created',               'Resource created'],
                  ['400', 'bad_request',           'Malformed request or invalid parameters'],
                  ['401', 'invalid_api_key',       'Missing or invalid API key'],
                  ['403', 'forbidden',             'Key lacks required scope'],
                  ['404', 'not_found',             'Resource does not exist'],
                  ['409', 'conflict',              'Resource already exists'],
                  ['422', 'unprocessable_entity',  'Validation failed'],
                  ['429', 'rate_limit_exceeded',   'Too many requests'],
                  ['500', 'internal_error',        'Server-side error — retry with backoff'],
                  ['503', 'service_unavailable',   'Temporarily offline — check status page'],
                ].map(([s, c, m]) => (
                  <tr key={c}>
                    <td><span className={`http-status s${s[0]}`}>{s}</span></td>
                    <td><code>{c}</code></td>
                    <td>{m}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {/* VERSIONING */}
          <section id="versioning" className="docs-section">
            <div className="section-eyebrow">API Lifecycle</div>
            <h2 className="docs-h2">Versioning</h2>
            <p>The API version is included in the URL path (<code>/v1/</code>). We maintain backwards compatibility within a major version. Breaking changes are released under a new version with a minimum 6-month deprecation window.</p>
            <div className="callout info">
              <strong>Current stable:</strong> <code>v1</code> — released January 2026.<br />
              Sunset policy: deprecated versions receive security patches only for 12 months post-sunset announcement.
            </div>
          </section>

          {/* PAGINATION */}
          <section id="pagination" className="docs-section">
            <div className="section-eyebrow">Lists</div>
            <h2 className="docs-h2">Pagination</h2>
            <p>All list endpoints use <strong>cursor-based pagination</strong> for consistency and performance.</p>
            <Code lang="bash">{`GET /v1/seo/keywords?limit=25&cursor=cur_9f3k2abc`}</Code>
            <Code lang="json">{`{
  "data": [ ... ],
  "pagination": {
    "has_more": true,
    "next_cursor": "cur_xj7p9def",
    "total_count": 842
  }
}`}</Code>
            <table className="param-table">
              <thead><tr><th>Parameter</th><th>Default</th><th>Max</th><th>Description</th></tr></thead>
              <tbody>
                <tr><td><code>limit</code></td><td>20</td><td>100</td><td>Number of results per page</td></tr>
                <tr><td><code>cursor</code></td><td>—</td><td>—</td><td>Opaque cursor from previous response</td></tr>
              </tbody>
            </table>
          </section>

          {/* SEO RANK ENGINE */}
          <section id="seo" className="docs-section">
            <div className="section-eyebrow">Product API</div>
            <h2 className="docs-h2">SEO Rank Engine</h2>
            <p>Track, query, and manage keyword rank positions across search engines.</p>
            <Endpoint method="GET" path="/v1/seo/keywords" desc="List all tracked keywords"
              params={[
                { name: 'project_id', type: 'string', req: true,  desc: 'Filter by project' },
                { name: 'engine',     type: 'string', req: false, desc: 'google | bing | duckduckgo' },
                { name: 'limit',      type: 'integer',req: false, desc: 'Results per page (max 100)' },
              ]}
              response={`{
  "data": [
    {
      "id": "kw_abc123",
      "keyword": "ai marketing tools",
      "position": 4,
      "previous_position": 11,
      "change": +7,
      "engine": "google",
      "updated_at": "2026-05-01T10:00:00Z"
    }
  ],
  "pagination": { "has_more": true, "next_cursor": "cur_xj7" }
}`}
            />
            <Endpoint method="POST" path="/v1/seo/keywords" desc="Add a keyword to track"
              body={`{
  "keyword": "ai marketing tools",
  "project_id": "proj_xyz",
  "engine": "google",
  "location": "US"
}`}
              response={`{
  "id": "kw_abc123",
  "keyword": "ai marketing tools",
  "status": "tracking",
  "created_at": "2026-05-01T10:00:00Z"
}`}
            />
            <Endpoint method="GET" path="/v1/seo/keywords/:id/history" desc="Get rank history for a keyword"
              params={[
                { name: 'id',    type: 'string', req: true,  desc: 'Keyword ID' },
                { name: 'from',  type: 'string', req: false, desc: 'ISO 8601 start date' },
                { name: 'to',    type: 'string', req: false, desc: 'ISO 8601 end date' },
              ]}
              response={`{
  "id": "kw_abc123",
  "keyword": "ai marketing tools",
  "history": [
    { "date": "2026-04-01", "position": 14 },
    { "date": "2026-04-15", "position": 9  },
    { "date": "2026-05-01", "position": 4  }
  ]
}`}
            />
            <Endpoint method="DELETE" path="/v1/seo/keywords/:id" desc="Stop tracking a keyword"
              params={[{ name: 'id', type: 'string', req: true, desc: 'Keyword ID' }]}
              response={`{ "deleted": true, "id": "kw_abc123" }`}
            />
          </section>

          {/* AI POSTER FORGE */}
          <section id="posters" className="docs-section">
            <div className="section-eyebrow">Product API</div>
            <h2 className="docs-h2">AI Poster Forge</h2>
            <p>Generate high-fidelity AI art and marketing posters programmatically.</p>
            <Endpoint method="POST" path="/v1/posters/generate" desc="Generate a new AI poster"
              body={`{
  "prompt": "Neon cityscape at night, cyberpunk aesthetic, teal and purple tones",
  "style": "cinematic",
  "aspect_ratio": "16:9",
  "resolution": "2048x1152",
  "format": "png"
}`}
              response={`{
  "id": "img_7xq9z",
  "status": "processing",
  "estimated_seconds": 8,
  "webhook_event": "poster.generated"
}`}
            />
            <Endpoint method="GET" path="/v1/posters/:id" desc="Get poster status and download URL"
              params={[{ name: 'id', type: 'string', req: true, desc: 'Poster ID' }]}
              response={`{
  "id": "img_7xq9z",
  "status": "complete",
  "url": "https://cdn.datatallied.com/posters/img_7xq9z.png",
  "expires_at": "2026-06-01T10:00:00Z",
  "metadata": {
    "prompt": "Neon cityscape...",
    "resolution": "2048x1152",
    "style": "cinematic"
  }
}`}
            />
            <Endpoint method="GET" path="/v1/posters" desc="List generated posters"
              params={[
                { name: 'limit',  type: 'integer', req: false, desc: 'Results per page' },
                { name: 'status', type: 'string',  req: false, desc: 'complete | processing | failed' },
              ]}
              response={`{
  "data": [ { "id": "img_7xq9z", "status": "complete", "created_at": "..." } ],
  "pagination": { "has_more": false }
}`}
            />
          </section>

          {/* STYLIZED QRs */}
          <section id="qr" className="docs-section">
            <div className="section-eyebrow">Product API</div>
            <h2 className="docs-h2">Stylized QRs</h2>
            <p>Generate branded, styled QR codes as SVG or PNG.</p>
            <Endpoint method="POST" path="/v1/qr/generate" desc="Create a stylized QR code"
              body={`{
  "url": "https://datatallied.web.app",
  "style": "rounded",
  "foreground": "#00CED1",
  "background": "#0A0A0A",
  "logo_url": "https://cdn.datatallied.com/logo.svg",
  "format": "svg",
  "error_correction": "H"
}`}
              response={`{
  "id": "qr_p2m8n",
  "url": "https://datatallied.web.app",
  "download_url": "https://cdn.datatallied.com/qr/qr_p2m8n.svg",
  "format": "svg",
  "created_at": "2026-05-01T10:00:00Z"
}`}
            />
            <Endpoint method="GET" path="/v1/qr/:id" desc="Retrieve a QR code"
              params={[{ name: 'id', type: 'string', req: true, desc: 'QR code ID' }]}
              response={`{
  "id": "qr_p2m8n",
  "url": "https://datatallied.web.app",
  "download_url": "https://cdn.datatallied.com/qr/qr_p2m8n.svg",
  "scans": 142,
  "created_at": "2026-05-01T10:00:00Z"
}`}
            />
          </section>

          {/* DATA EXPORT */}
          <section id="export" className="docs-section">
            <div className="section-eyebrow">Data Portability</div>
            <h2 className="docs-h2">Data Export</h2>
            <p>Export any dataset from the platform as CSV, JSON, or Parquet. Exports are async — you receive a URL when processing completes.</p>
            <Endpoint method="POST" path="/v1/export" desc="Trigger a data export"
              body={`{
  "resource": "seo.keywords",
  "format": "json",
  "filters": {
    "project_id": "proj_xyz",
    "from": "2026-01-01",
    "to": "2026-05-01"
  },
  "notify_webhook": true
}`}
              response={`{
  "export_id": "exp_9f3k2",
  "status": "queued",
  "resource": "seo.keywords",
  "format": "json",
  "estimated_seconds": 3,
  "created_at": "2026-05-01T10:00:00Z"
}`}
            />
            <Endpoint method="GET" path="/v1/export/:export_id" desc="Check export status"
              params={[{ name: 'export_id', type: 'string', req: true, desc: 'Export job ID' }]}
              response={`{
  "export_id": "exp_9f3k2",
  "status": "complete",
  "download_url": "https://cdn.datatallied.com/exports/exp_9f3k2.json",
  "expires_at": "2026-05-02T10:00:00Z",
  "row_count": 1482,
  "size_bytes": 284920
}`}
            />
            <div className="callout info">
              <strong>Supported resources:</strong><code> seo.keywords</code> · <code>seo.history</code> · <code>posters</code> · <code>qr_codes</code> · <code>webhooks.events</code> · <code>billing.usage</code>
            </div>
            <h3 className="docs-h3">Supported Formats</h3>
            <table className="param-table">
              <thead><tr><th>Format</th><th>MIME Type</th><th>Notes</th></tr></thead>
              <tbody>
                <tr><td><code>json</code></td><td><code>application/json</code></td><td>Newline-delimited JSON (NDJSON)</td></tr>
                <tr><td><code>csv</code></td><td><code>text/csv</code></td><td>RFC 4180, UTF-8</td></tr>
                <tr><td><code>parquet</code></td><td><code>application/octet-stream</code></td><td>Apache Parquet v2 (recommended for large datasets)</td></tr>
              </tbody>
            </table>
          </section>

          {/* WEBHOOKS */}
          <section id="webhooks" className="docs-section">
            <div className="section-eyebrow">Real-time Events</div>
            <h2 className="docs-h2">Webhooks</h2>
            <p>Register HTTPS endpoints to receive real-time event notifications. We deliver events with a signed payload and automatic retry with exponential backoff.</p>

            <h3 className="docs-h3">Event Types</h3>
            <table className="param-table">
              <thead><tr><th>Event</th><th>Trigger</th></tr></thead>
              <tbody>
                {[
                  ['rank.updated',       'A tracked keyword changes position'],
                  ['rank.alert',         'Keyword drops or rises past a threshold'],
                  ['poster.generated',   'AI poster generation complete'],
                  ['poster.failed',      'AI poster generation failed'],
                  ['qr.created',         'QR code created'],
                  ['export.complete',    'Data export file is ready to download'],
                  ['export.failed',      'Export job encountered an error'],
                  ['webhook.test',       'Test event fired from dashboard'],
                ].map(([e, t]) => (
                  <tr key={e}><td><code>{e}</code></td><td>{t}</td></tr>
                ))}
              </tbody>
            </table>

            <Endpoint method="POST" path="/v1/webhooks" desc="Register a new webhook endpoint"
              body={`{
  "url": "https://yourapp.com/hooks/datatallied",
  "events": ["rank.updated", "export.complete", "poster.generated"],
  "secret": "whsec_your_signing_secret"
}`}
              response={`{
  "id": "wh_4j9pq",
  "url": "https://yourapp.com/hooks/datatallied",
  "events": ["rank.updated", "export.complete", "poster.generated"],
  "status": "active",
  "created_at": "2026-05-01T10:00:00Z"
}`}
            />
            <Endpoint method="GET" path="/v1/webhooks" desc="List registered webhooks"
              params={[{ name: 'limit', type: 'integer', req: false, desc: 'Results per page' }]}
              response={`{
  "data": [
    { "id": "wh_4j9pq", "url": "https://yourapp.com/hooks/datatallied", "status": "active" }
  ]
}`}
            />
            <Endpoint method="DELETE" path="/v1/webhooks/:id" desc="Remove a webhook endpoint"
              params={[{ name: 'id', type: 'string', req: true, desc: 'Webhook ID' }]}
              response={`{ "deleted": true, "id": "wh_4j9pq" }`}
            />

            <h3 className="docs-h3">Payload Signature</h3>
            <p>Every request includes a <code>DT-Signature</code> header. Verify it to ensure the payload is authentic:</p>
            <Code lang="javascript">{`const crypto = require('crypto');

function verifySignature(payload, signature, secret) {
  const expected = crypto
    .createHmac('sha256', secret)
    .update(payload)
    .digest('hex');
  return crypto.timingSafeEqual(
    Buffer.from(signature),
    Buffer.from('sha256=' + expected)
  );
}`}</Code>

            <h3 className="docs-h3">Retry Policy</h3>
            <table className="param-table">
              <thead><tr><th>Attempt</th><th>Delay</th></tr></thead>
              <tbody>
                <tr><td>1st retry</td><td>5 seconds</td></tr>
                <tr><td>2nd retry</td><td>30 seconds</td></tr>
                <tr><td>3rd retry</td><td>5 minutes</td></tr>
                <tr><td>4th retry</td><td>30 minutes</td></tr>
                <tr><td>5th retry</td><td>2 hours (final)</td></tr>
              </tbody>
            </table>
            <div className="callout warn">
              Your endpoint must return a <code>2xx</code> status within <strong>10 seconds</strong>. Non-2xx or timeouts are treated as failures and trigger the retry schedule above.
            </div>
          </section>

        </main>
      </div>
    </div>
  );
}
